import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SparklesIcon } from '@heroicons/react/24/solid';

export interface OutOfWordsInfo {
  balance: number;
  needed: number;
  plan: string;
}

/** True when an API error is the 402 `out_of_words` gate. */
export function outOfWordsFrom(err: any): OutOfWordsInfo | null {
  const data = err?.response?.data;
  if (err?.response?.status === 402 && data?.code === 'out_of_words') {
    return {
      balance: Number(data.balance) || 0,
      needed: Number(data.needed) || 0,
      plan: data.plan || 'free',
    };
  }
  return null;
}

/**
 * A collaborator's 402 about the OWNER's words (empty balance, or this month's share used).
 * Their own top-up sheet wouldn't help, so this is a message, not the sheet.
 */
export function sharedWordsMessage(err: any, t: (key: string, fallback: string) => string) {
  const code = err?.response?.status === 402 ? err?.response?.data?.code : null;
  if (code === 'owner_cap_reached') {
    return t(
      'stories.ai.ownerCapReached',
      "You've used this month's share of the owner's AI words. Switch to your own words under People, or ask the owner to raise the limit.",
    );
  }
  if (code === 'owner_out_of_words') {
    return t(
      'stories.ai.ownerOutOfWords',
      "The story owner's AI words have run out. Switch to your own words under People, or ask them to top up.",
    );
  }
  return null;
}

interface Props {
  info: OutOfWordsInfo;
  onClose: () => void;
}

/**
 * The moment a writer runs out of AI words mid-scene. Friendly, two clear ways forward,
 * never a bare error — this is where a free writer becomes a paying one.
 */
const OutOfWordsSheet: React.FC<Props> = ({ info, onClose }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isFree = info.plan === 'free';

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4">
      <div
        role="dialog"
        aria-labelledby="oow-title"
        className="w-full max-w-sm rounded-2xl p-6"
        style={{ background: 'var(--ds-surface, #fff)', color: 'var(--ds-text-primary)' }}
      >
        <div className="flex items-center justify-center w-12 h-12 bg-primary-500/20 rounded-full mx-auto mb-4">
          <SparklesIcon className="w-6 h-6 text-primary-500" />
        </div>
        <h3 id="oow-title" className="text-lg font-bold text-center mb-2">
          {isFree
            ? t('outOfWords.titleFree', "You've used your free AI words")
            : t('outOfWords.title', "You're out of AI words")}
        </h3>
        <p className="text-sm text-center mb-1" style={{ color: 'var(--ds-text-secondary)' }}>
          {t(
            'outOfWords.body',
            'This request needs about {{needed}} words and you have {{balance}} left.',
            {
              needed: info.needed.toLocaleString(),
              balance: info.balance.toLocaleString(),
            },
          )}
        </p>
        <p className="text-sm text-center mb-6" style={{ color: 'var(--ds-text-secondary)' }}>
          {isFree
            ? t(
                'outOfWords.hintFree',
                'Keep the momentum — a plan refills every month, a top-up never expires.',
              )
            : t(
                'outOfWords.hint',
                'Top up now or move to a bigger plan; your draft stays exactly where it is.',
              )}
        </p>
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => navigate('/subscription')}
            className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-xl transition-colors text-sm"
          >
            {isFree
              ? t('outOfWords.choosePlan', 'Choose a plan')
              : t('outOfWords.upgrade', 'Upgrade plan')}
          </button>
          <button
            type="button"
            onClick={() => navigate('/credits')}
            className="w-full py-3 bg-primary-500/10 hover:bg-primary-500/20 text-primary-500 font-semibold rounded-xl transition-colors text-sm"
          >
            {t('outOfWords.topUp', 'Top up words')}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2 text-sm"
            style={{ color: 'var(--ds-text-secondary)' }}
          >
            {t('outOfWords.later', 'Not now')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OutOfWordsSheet;
